/**
 * `X-Access-Reason` — the header three of this build's screens cannot read without.
 *
 * Spec 02 §11 step 7, and `API_CONTRACT.md` §8.7: the reason is recorded in
 * `wp_data_access_logs` beside the operator's name, the rows they touched and the
 * endpoint they touched them through. `openapi.yaml` describes the header as
 * "required by policy for some resources"; on `GET /alerts` and on all five incident
 * operations that resolves to **always** in v1, because no policy table can turn it
 * off and `wp_data_access_reason_ck` refuses a `sensitive` access-log row without one.
 *
 * ── Why the screens gate on it instead of sending the read and rendering the 403 ──
 *
 * Because a screen that fires a request it knows will be refused teaches the person
 * reading it that refusals are background noise. `PRODUCT.md` §4's whole argument is
 * that a state means something; a permission-denied state that appears on every first
 * paint means nothing. The reason field is therefore the **first** thing on each of
 * these screens and no request goes out before it is satisfied.
 *
 * ── Why the client checks the length at all ──
 *
 * `MIN_ACCESS_REASON_LENGTH` is 10, **after trimming**, and it is enforced twice on
 * the server: once in the authoriser, so the caller gets a `403
 * WORKFORCE_ACCESS_REASON_REQUIRED`, and once by the CHECK constraint, which is the
 * control that still holds when the authoriser is gone. Checking it here is not a
 * third authorisation path — nothing here decides whether the read happens on the
 * server — it is so the operator learns the rule from the form rather than from a
 * refusal, which is the same argument Task 45 made for the revoke reason.
 *
 * The number is duplicated from `src/lib/audit.ts` and `reason.test.ts` asserts the
 * two agree by reading that file, so it cannot drift into being more permissive than
 * the constraint.
 */

/** `MIN_ACCESS_REASON_LENGTH` of `src/lib/audit.ts`. Ten characters, after trimming. */
export const MIN_ACCESS_REASON = 10;

/** `AccessReason`'s `maxLength` in `openapi.yaml`. */
export const MAX_ACCESS_REASON = 512;

/**
 * ── A header value is a ByteString, and Vietnamese does not fit in one ──────
 *
 * Found by this task's own tests, and it is a real defect rather than a test
 * artefact: `Headers.set` throws
 * `TypeError: Cannot convert argument to a ByteString because the character at index
 * 0 has a value of 273` on `điều tra…`, because RFC 9110 field values are
 * ISO-8859-1 and `đ` is U+0111. The same throw happens in a browser, so a Vietnamese
 * reason does not merely arrive wrong — **the request is never sent at all**, and the
 * operator sees a transport failure with no diagnosis.
 *
 * There is no encoding this client may apply on its own. Percent-encoding or RFC 8187
 * would send something the server does not decode, so `wp_data_access_logs.reason`
 * would hold `%C4%91i%E1%BB%81u…` where a human is supposed to read a sentence — a
 * silently wrong audit record, which is worse than a refused one. `src/lib/audit.ts`
 * measures and stores the header verbatim and defines no transfer encoding.
 *
 * So this fails **closed**: a reason outside Latin-1 is refused here, in the field,
 * with the reason said out loud, and the unaccented spelling — which every Vietnamese
 * system accepts — goes through unchanged. **This is a server-side gap and it is
 * reported rather than papered over**: an endpoint whose only mandatory human input
 * cannot carry the product's primary language needs an encoding decided by whoever
 * owns `src/lib/audit.ts`, not by a client picking one.
 *
 * Code points 128–255 are allowed and are correct: Node parses incoming header bytes
 * as Latin-1, so those round-trip exactly.
 */
export function isHeaderSafe(value: string): boolean {
  // Iterated by code point rather than by UTF-16 unit, so an astral character is
  // judged once and correctly rather than as two surrogates that are each below 0xff.
  for (const character of value) {
    const point = character.codePointAt(0);
    if (point === undefined || point > 0xff) return false;
  }
  return true;
}

export function isUsableReason(value: string): boolean {
  const trimmed = value.trim();
  return (
    trimmed.length >= MIN_ACCESS_REASON &&
    trimmed.length <= MAX_ACCESS_REASON &&
    isHeaderSafe(trimmed)
  );
}

/**
 * The header object for a read, or `undefined` when there is no usable reason.
 *
 * `undefined` rather than `{}` so that a caller passing it straight into
 * `useApiQuery` cannot accidentally issue the request with an empty header: the
 * screens pass `null` as the *path* when there is no reason, which is the hook's own
 * "there is nothing to ask yet" signal, and this function exists so the two decisions
 * are made from one predicate.
 *
 * The value is sent **trimmed**, because the server trims before measuring and a
 * reason that passed here on leading whitespace would be refused there — a refusal
 * whose cause is invisible in the field the operator is looking at.
 */
export function accessHeaders(reason: string): Readonly<Record<string, string>> | undefined {
  if (!isUsableReason(reason)) return undefined;
  return { 'X-Access-Reason': reason.trim() };
}
